import { Factory, LanguageCode, Settings } from '..'
import { CountryCode } from '../enum/country-code'
import { Deletable } from '../interface/deletable'
import { Modifiable } from '../interface/modifiable'
import { I18nString } from './i18nstring'
import { LastHardwareContact } from './last-hardware-contact'
import { Locker, PaymentTerminal, PaymentTerminalType } from './locker'
import { Firmware } from './firmware'
import { rememberPayload, sourceJson } from '../../_utils/fcdc-v2'

export class Column implements Deletable, Modifiable {
  id: string
  countryCode: CountryCode
  customerId: string
  presenceId: string
  siteId: string
  locationId?: string
  hardwareId?: string
  names: I18nString[]
  note?: string
  lockers: Locker[]
  settings?: Settings
  lastHardwareContact?: LastHardwareContact
  hideFromWatchdog: boolean
  deleted: boolean
  firmware: Firmware
  created: Date
  modified: Date

  constructor(json: any) {
    this.id = json.id
    this.countryCode = json.countryCode
    this.customerId = json.customerId
    this.presenceId = json.presenceId
    this.locationId = json.locationId
    this.siteId = json.siteId
    this.hardwareId = json.hardwareId
    this.lastHardwareContact = json.lastHardwareContact
      ? new LastHardwareContact(json.lastHardwareContact)
      : undefined
    this.hideFromWatchdog = json.hideFromWatchdog
      ? Boolean(json.hideFromWatchdog)
      : false
    this.names = json.names
      ? json.names.map((name: any) => new I18nString(name))
      : []
    this.note = json.note
    this.lockers = json.lockers
      ? json.lockers.map((locker: Locker) => new Locker(locker))
      : []
    this.settings = json.settings
      ? new Settings(sourceJson(json.settings))
      : undefined
    this.deleted = json.deleted ?? false
    this.firmware = json.firmware
      ? new Firmware(json.firmware)
      : new Firmware({})
    this.created = new Date(json.created)
    this.modified = new Date(json.modified)
    rememberPayload(this, json)
  }

  static getFactory(): Factory<Column> {
    return new (class implements Factory<Column> {
      make(json: any): Column {
        return new Column(json)
      }

      getTableName(): string {
        return 'columns'
      }
    })()
  }

  static getUrl(siteId: string, columnId?: string): string {
    return `/sites/${siteId}/columns` + (columnId ? '/' + columnId : '')
  }

  async getLocker(id: string): Promise<Locker | undefined> {
    return this.lockers.find(locker => locker.id === id) ?? undefined
  }

  getPaymentTerminal(): PaymentTerminal | undefined {
    return this.lockers.find(locker => locker.paymentTerminal)?.paymentTerminal
  }

  getPaymentTerminalKind():
    | PaymentTerminalType.MonoDevice
    | PaymentTerminalType.DuoDevice
    | undefined {
    return this.getPaymentTerminal()?.getKind()
  }

  getPaymentTerminalPositionIndex(): number | undefined {
    const lockerIndex = this.lockers.findIndex(locker => locker.paymentTerminal)
    if (lockerIndex < 0) {
      return undefined
    }
    return lockerIndex + 1
  }

  getColumnName(l?: LanguageCode): string {
    return (
      this.names.find(name => name.language === l)?.value ?? this.names[0].value
    )
  }

  async isProvisioned(): Promise<boolean> {
    return (
      this.lastHardwareContact !== undefined &&
      this.hardwareId !== undefined &&
      this.locationId !== undefined &&
      !this.deleted
    )
  }

  async isFirmwareValid(): Promise<boolean> {
    return this.firmware.version !== undefined && this.firmware.id !== undefined
  }
}
